import PrimaryButton from "components/buttons/primary-button";
import Typography from "components/typography";
import React from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import WalletImage from "images/png/walletillustration.png";
import StoresLogos from "images/png/landingPageStores.png";
import UploadIllustration from "images/png/uploadIllustration.png";
import InsightsMap from "images/png/insightsMap.png";
import RoyaltyIllustration from "images/png/royaltyIllustration.png";
import DistroIllustration from "images/png/landing-page/music-distro.png";
import { useIntersectionObserver } from "hooks/UseIntersectionObserver";

const Qualities = () => {
  return (
    <Container>
      <Section>
        <Texts width="50%">
          <Typography
            size="4rem"
            m_size="3xl"
            weight="bold"
            mb="2"
            color={Colors.grey300}
            dataAos="fade-up"
          >
            Your Music, Everywhere That <br /> Matters
          </Typography>
          <Typography
            color={Colors.grey400}
            mb="3.2"
            size="xl"
            m_size="m"
            dataAos="fade-up"
          >
            One upload. All the platforms that count. Zero hassle.
          </Typography>
          <PrimaryButton
            text="View All Stores"
            fitContent
            inset
            sparkle
            data-aos="fade-up"
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="100%"
                height="100%"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M3.33398 10H16.6673M16.6673 10L11.6673 5M16.6673 10L11.6673 15"
                  stroke="#131313"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            }
            iconPosition="right"
            iconSize="2"
          />
        </Texts>
        <Image
          width="50%"
          alt="stores"
          src={StoresLogos}
          data-aos="fade-left"
        />
      </Section>
      <Section>
        <Texts width="50%">
          <Typography
            size="4rem"
            weight="bold"
            mb="2"
            m_size="3xl"
            color={Colors.grey300}
            dataAos="fade-up"
          >
            Unlimited Music & Video Distribution
          </Typography>
          <Typography
            color={Colors.grey400}
            mb="3.2"
            size="xl"
            m_size="m"
            dataAos="fade-up"
          >
            With Rated Afrika, share your artistry without limits. Enjoy
            unlimited music and video uploads that reach the world’s top
            platforms, including Spotify, Apple Music, YouTube Music, and more.
            Whether you’re releasing singles, albums, or exclusive music videos,
            we give you the freedom to keep your creativity flowing, without
            constraints.
          </Typography>
        </Texts>
        <Image
          width="35%"
          alt="distro"
          src={DistroIllustration}
          data-aos="fade-left"
        />
      </Section>
      <Section reverse="true">
        <Image
          width="35%"
          src={WalletImage}
          alt="wallet"
          data-aos="fade-right"
        />
        <Texts width="50%">
          <WalletHeading data-aos="fade-up">
            <Typography size="4rem" m_size="3xl" weight="bold" mr="1">
              Keep
            </Typography>
            <PercentText>
              <Typography size="4rem" m_size="3xl" weight="bold" mr="1">
                100%
              </Typography>
              <PercentStroke>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="100%"
                  height="100%"
                  viewBox="0 0 107 11"
                  fill="none"
                >
                  <path
                    d="M1.66016 2.28003H105.195M17.6553 9.40601H87.7755M18.1015 9.40601L79.0663 2.45233"
                    stroke="#FEB73D"
                    stroke-width="3"
                    stroke-linecap="round"
                  />
                </svg>
              </PercentStroke>
            </PercentText>

            <Typography size="4rem" m_size="3xl" weight="bold">
              Of What You Earn
            </Typography>
          </WalletHeading>
          <Typography
            size="xl"
            m_size="m"
            color={Colors.grey400}
            dataAos="fade-up"
          >
            Your art, your earnings. At Rated Afrika, we believe that every cent
            from your hard work belongs to you. That’s why we offer a platform
            where artists keep 100% of their royalties, with no hidden fees or
            cuts. Enjoy the freedom to release your music and videos while
            staying fully in control of your revenue.
          </Typography>
        </Texts>
      </Section>
      <Section>
        <Texts width="45%">
          <Typography
            size="4rem"
            weight="bold"
            mb="2"
            m_mb="1.6"
            m_size="3xl"
            dataAos="fade-up"
          >
            Easy Upload & Release Management
          </Typography>
          <Typography
            size="xl"
            color={Colors.grey400}
            m_size="m"
            dataAos="fade-up"
          >
            Simplify your music and video distribution with our all-in-one
            upload system. Easily manage your metadata, schedule precise release
            dates, and track your content's progress across platforms - so you
            can focus on creating great music.
          </Typography>
        </Texts>
        <Image
          width="35%"
          alt="upload"
          src={UploadIllustration}
          data-aos="fade-left"
        />
      </Section>
      <Section reverse="true">
        <Image
          width="45%"
          alt="insights"
          src={InsightsMap}
          data-aos="fade-right"
        />
        <Texts width="45%">
          <Typography
            size="4rem"
            weight="bold"
            mb="2"
            m_size="3xl"
            m_mb="1.6"
            dataAos="fade-up"
          >
            Know Your Fans: Insights & Stats
          </Typography>
          <Typography
            size="xl"
            color={Colors.grey400}
            m_size="m"
            dataAos="fade-up"
          >
            Get to know your fans like never before! With Rated Afrika, you can
            dive into detailed stats that track every stream, download, and view
            across all major platforms. See where your audience is tuning in
            from, what songs they love, and how often they’re hitting replay.
          </Typography>
        </Texts>
      </Section>
      <Section>
        <Texts width="42%">
          <Typography
            size="4rem"
            weight="bold"
            mb="2"
            m_size="3xl"
            m_mb="1.6"
            dataAos="fade-up"
          >
            Royalty Splits Made Simple
          </Typography>
          <Typography
            size="xl"
            color={Colors.grey400}
            m_size="m"
            dataAos="fade-up"
          >
            Track your royalties in real-time across all platforms, so you know
            exactly what you’ve earned and where it’s coming from. Easily set up
            custom royalty splits for each track, so every contributor. Whether
            producer, engineer, collaborator or fellow Artists gets their fair
            share.
          </Typography>
        </Texts>
        <Image
          width="45%"
          alt="insights"
          src={RoyaltyIllustration}
          data-aos="fade-left"
        />
      </Section>
    </Container>
  );
};

export default Qualities;

const Container = styled.div`
  padding: 0 10.4rem;
  @media only screen and (max-width: 769px) {
    padding: 0 2rem;
  }
`;

const Section = styled.div<{ reverse?: string }>`
  width: 100%;
  min-height: 53.2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  @media only screen and (max-width: 769px) {
    flex-direction: ${(props) =>
      props.reverse === "true" ? "column-reverse" : "column"};
    min-height: fit-content;
    margin-bottom: 3.2rem;
  }
`;

const Texts = styled.div<{ width: string; m_width?: string }>`
  width: ${(props) => props.width};
  @media only screen and (max-width: 769px) {
    width: 100%;
  }
`;

const Image = styled.img<{ width: string; m_width?: string }>`
  width: ${(props) => props.width};
  @media only screen and (max-width: 769px) {
    width: 100%;
  }
`;

const WalletHeading = styled.div`
  display: inline-flex;
  margin-bottom: 2rem;
`;
const PercentText = styled.div`
  position: relative;
`;
const PercentStroke = styled.div`
  position: absolute;
  width: 10.7rem;
  height: 1rem;
  bottom: 0;
`;
